import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@pantry/ui';
import { XMarkIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import type { BatchDraftItem, BatchDraftInstance, ItemFormMode } from '@pantry/shared';
import { UnifiedItemForm } from '../items/unified-form';
import {
  batchDraftToCreateItemValues,
  batchDraftToEditItemValues,
  batchDraftToInstanceValues,
  createItemValuesToPartialBatchDraft,
  editItemValuesToPartialBatchDraft,
  instanceValuesToPartialInstance,
  instanceValuesToNewBatch,
} from '@pantry/shared/batch';

interface BatchItemEditSheetProps {
  item: BatchDraftItem | null;
  householdId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<BatchDraftItem>) => void;
  /** If set, editing a specific instance within the item */
  editingInstanceId?: string | null;
  /** Callback to update a specific instance */
  onSaveInstance?: (instanceId: string, updates: Partial<BatchDraftInstance>) => void;
  /** If true, we're adding a new batch to an existing item */
  isAddingBatch?: boolean;
  /** Callback to add a new batch to an existing item */
  onAddBatch?: (instance: Omit<BatchDraftInstance, 'instanceId'>) => void;
}

/**
 * Bottom sheet for editing a draft item's details.
 * Wraps UnifiedItemForm with batch-specific mapping logic.
 */
export function BatchItemEditSheet({
  item,
  householdId,
  isOpen,
  onClose,
  onSave,
  editingInstanceId,
  onSaveInstance,
  isAddingBatch,
  onAddBatch,
}: BatchItemEditSheetProps) {
  // Determine which mode to use
  const mode: ItemFormMode = isAddingBatch
    ? 'create-instance'
    : editingInstanceId
      ? 'edit-instance'
      : isManualOrUnknown(item)
        ? 'create-item'
        : 'edit-item';

  // Build the editing instance for edit-instance mode
  const editingInstance = useMemo(() => {
    if (!editingInstanceId || !item?.instances) return undefined;
    return item.instances.find((i) => i.instanceId === editingInstanceId);
  }, [editingInstanceId, item]);

  // Build initial form values based on mode
  const initialValues = useMemo(() => {
    if (!item) return undefined;

    if (mode === 'create-instance') {
      return batchDraftToInstanceValues(item);
    }
    if (mode === 'edit-instance') {
      return batchDraftToInstanceValues(item, editingInstanceId || undefined);
    }
    if (mode === 'create-item') {
      return batchDraftToCreateItemValues(item);
    }
    return batchDraftToEditItemValues(item);
  }, [item, mode, editingInstanceId]);

  if (!isOpen || !item || !initialValues) return null;

  // Determine sheet title
  const sheetTitle = isAddingBatch ? (
    <Trans>Add Another Batch</Trans>
  ) : editingInstanceId ? (
    <Trans>Edit Batch</Trans>
  ) : (
    <Trans>Edit Item</Trans>
  );

  // Build mode-specific props for UnifiedItemForm
  const formProps = buildFormProps(
    mode,
    item,
    editingInstance,
    householdId,
    onClose,
    onSave,
    onSaveInstance,
    onAddBatch,
    editingInstanceId,
    mode === 'create-item'
      ? (initialValues as import('@pantry/shared').CreateItemFormValues | undefined)
      : undefined
  );

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/20 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl shadow-xl animate-in slide-in-from-bottom duration-300 max-h-[85vh] overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 sticky top-0 bg-background z-10 border-b border-border/30">
            <CardTitle className="font-display text-lg font-semibold">{sheetTitle}</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 rounded-full hover:bg-muted"
              onClick={onClose}
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </CardHeader>

          <CardContent className="space-y-4 pb-8">
            {/* Instance mode header - show product info but don't allow editing */}
            {(isAddingBatch || editingInstanceId) && <InstanceModeHeader item={item} />}

            <UnifiedItemForm {...formProps} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Read-only header showing product info in instance editing modes.
 */
function InstanceModeHeader({ item }: { item: BatchDraftItem }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 border border-border/50">
      {item.displayImageUrl ? (
        <img src={item.displayImageUrl} alt="" className="h-12 w-12 rounded-lg object-cover" />
      ) : (
        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
          <span className="text-xs text-muted-foreground">
            <Trans>No img</Trans>
          </span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{item.displayName}</p>
        {item.displayBrand && (
          <p className="text-xs text-muted-foreground truncate">{item.displayBrand}</p>
        )}
        {item.instances && item.instances.length > 1 && (
          <div className="flex items-center gap-1 mt-1 text-xs text-primary">
            <Square2StackIcon className="h-3 w-3" />
            <Trans>{item.instances.length} batches</Trans>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Check if a draft item is a manual entry or unknown scanned item
 * that needs product search / name input (create-item mode).
 */
function isManualOrUnknown(item: BatchDraftItem | null): boolean {
  if (!item) return false;
  const isManualEntry = !item.productId && !item.barcode;
  const isUnknownScannedItem =
    !item.productId && !!item.barcode && (item.displayName?.startsWith('Barcode:') ?? false);
  return isManualEntry || isUnknownScannedItem;
}

/**
 * Build the discriminated union props for UnifiedItemForm based on mode.
 */
function buildFormProps(
  mode: ItemFormMode,
  item: BatchDraftItem,
  editingInstance: BatchDraftInstance | undefined,
  householdId: string,
  onClose: () => void,
  onSave: (updates: Partial<BatchDraftItem>) => void,
  onSaveInstance: ((instanceId: string, updates: Partial<BatchDraftInstance>) => void) | undefined,
  onAddBatch: ((instance: Omit<BatchDraftInstance, 'instanceId'>) => void) | undefined,
  editingInstanceId: string | null | undefined,
  createItemInitialValues?: import('@pantry/shared').CreateItemFormValues
): React.ComponentProps<typeof UnifiedItemForm> {
  const common = {
    householdId,
    onCancel: onClose,
  };

  if (mode === 'create-item') {
    return {
      ...common,
      mode: 'create-item',
      initialValues: createItemInitialValues,
      onSubmit: async (values) => {
        onSave(createItemValuesToPartialBatchDraft(values));
        onClose();
      },
    };
  }

  if (mode === 'edit-item') {
    // Build a minimal ItemWithRelations from the BatchDraftItem for UnifiedItemForm
    const itemWithRelations = batchDraftToItemWithRelations(item);
    return {
      ...common,
      mode: 'edit-item',
      item: itemWithRelations,
      onSubmit: async (values) => {
        onSave(editItemValuesToPartialBatchDraft(values, item.displayName));
        onClose();
      },
    };
  }

  if (mode === 'create-instance') {
    return {
      ...common,
      mode: 'create-instance',
      onSubmit: async (values) => {
        onAddBatch?.(instanceValuesToNewBatch(values));
        onClose();
      },
    };
  }

  // edit-instance
  const now = new Date();
  const instanceForForm: import('@pantry/shared').ItemInstance = editingInstance
    ? {
        id: editingInstance.instanceId,
        itemId: item.clientId,
        quantity: editingInstance.quantity,
        purchaseDate: editingInstance.purchaseDate || null,
        purchasePrice: null,
        expiryDate: editingInstance.expiryDate || null,
        notes: editingInstance.notes || null,
        status: 'active',
        statusNotes: null,
        statusChangedAt: null,
        statusChangedBy: null,
        wasteReason: null,
        consumeReason: null,
        createdAt: new Date(editingInstance.addedAt),
        updatedAt: new Date(editingInstance.addedAt),
      }
    : {
        id: '',
        itemId: item.clientId,
        quantity: 1,
        purchaseDate: null,
        purchasePrice: null,
        expiryDate: null,
        notes: null,
        status: 'active',
        statusNotes: null,
        statusChangedAt: null,
        statusChangedBy: null,
        wasteReason: null,
        consumeReason: null,
        createdAt: now,
        updatedAt: now,
      };

  return {
    ...common,
    mode: 'edit-instance',
    instance: instanceForForm,
    onSubmit: async (values) => {
      if (editingInstanceId && onSaveInstance) {
        onSaveInstance(editingInstanceId, instanceValuesToPartialInstance(values));
      }
      onClose();
    },
  };
}

/**
 * Build a minimal ItemWithRelations shape from a BatchDraftItem.
 * Used for the edit-item mode of UnifiedItemForm.
 */
function batchDraftToItemWithRelations(
  item: BatchDraftItem
): import('@pantry/shared').ItemWithRelations {
  const addedDate = new Date(item.addedAt);
  const now = new Date();

  return {
    id: item.clientId,
    householdId: '',
    productId: item.productId || null,
    customName: item.customName || null,
    unit: item.unit,
    quantity: item.quantity,
    storageLocationId: item.storageLocationId || null,
    notes: item.notes || null,
    targetQuantity: null,
    targetUnit: null,
    lowStockThreshold: null,
    expiryThresholdDays: null,
    createdFrom: 'web',
    createdAt: addedDate,
    updatedAt: addedDate,
    product: item.productId
      ? {
          id: item.productId,
          barcode: item.barcode || null,
          name: item.displayName,
          brand: item.displayBrand || null,
          description: null,
          imageUrl: item.displayImageUrl || null,
          category: null,
          nutritionData: null,
          source: 'user',
          sourceId: null,
          defaultShelfLifeDays: null,
          defaultQuantity: null,
          defaultUnit: null,
          rawUnit: null,
          names: null,
          ingredientsText: null,
          allergensTags: null,
          labelsTags: null,
          ecoscoreGrade: null,
          nutriscoreGrade: null,
          novaGroup: null,
          images: null,
          offLastModified: null,
          offCategoryTags: null,
          createdAt: now,
          updatedAt: now,
        }
      : null,
    storageLocation: null,
  };
}
